/**
 * Site-nav / menu-entry icons (stroke-based, `currentColor` throughout — the host button or nav
 * link recolors every one of them): the marks that identify a navigation destination or a pause-
 * menu entry, as opposed to `prompt-icons.js`'s bidding/declaration tiles and universal chrome
 * (the menu button, the fullscreen corners, the play/quit gate pair). Split out of
 * `prompt-icons.js` (nav-icon-candidate-placements task, 2026-08-17) once that file's own growth —
 * the site-nav Features round graduating four more candidates, plus the credential mark — pushed
 * it over the JS module size budget (CODING.md § Size Limits); this is the SRP boundary the split
 * follows, not an arbitrary line count: "where you navigate" vs. "what happens during a hand."
 *
 * `createKeep` (Features), `createLantern` (Your games), `createFingerpost` (Rules),
 * `createQuarteredShield` (Help), and `createCrossedKeys` (the credential mark, four homes outside
 * the nav row) are the site-nav Features round's five candidates, all now placed — see
 * docs/components/MARKS.md § The Features mark / § The Your-games mark / § The Rules mark /
 * § The Help mark / § Keys. `createScroll` and `createRulebook` both retired from the nav (Help,
 * Rules) but stay defined and importable per dev/CLAUDE.md's no-pruning rule; only `createRulebook`
 * is still parked on the icon-lab Prompt & Feat tab — `createScroll` has headed the Menu's
 * Nachschlagen column since profile-overlay-and-menu-slim (see its own entry below).
 * `createCompass` (Strategy), `createTabbedTome`
 * (Glossary), `createThreeLeggedStool` (Multiplayer), and `createVictorDais` (Leaderboard) round
 * out the eight-token `site-nav-enhance.js` map (nine with `createSpectacles`, which stays in
 * `prompt-icons.js` as a button-context factory rather than moving here — it isn't a nav-identity
 * mark, it's Lothar's own likeness, § Spectacles).
 */
import { SVG_NS, svg, path, line, filledDot, filledPath } from "./svg.js";

/** Site-nav Features entry: a castle silhouette — two flanking towers and a central keep over a
 *  filled gate arch. The idea: the Features family explains what this app IS, so the mark is the
 *  whole fortress rather than one thing inside it. Distinct from `createGatehouse` (one arch under
 *  one battlement, and a role icon meaning "the declarer"), from `createMenuIcon` (crown over two
 *  wall courses) and from `createGateEnter` (silhouette + arrow): three roof lines at three
 *  heights is the read here. The gate is a filled mass — the family's fill-plus-stroke contrast
 *  (the rulebook's clasp, the compass's needle) — and the one detail that survives 20px.
 *  currentColor, like the menu-nav marks beside it (createFingerpost/createQuarteredShield/
 *  createCompass) — the host recolors it. Chosen from a five-candidate round; the other four have
 *  since shipped elsewhere in the nav (nav-icon-candidate-placements task — see createLantern,
 *  createFingerpost, createQuarteredShield, and createCrossedKeys below), all still browsable
 *  together on the icon-lab Proposals tab (`dev/nav-icon-candidates-spike.js`). See
 *  docs/components/MARKS.md § The Features mark. */
export function createKeep(size) {
  const el = svg(size, size);
  // Two flanking towers — coarse two-merlon crowns (4-unit merlons; anything
  // finer is under 2px at the nav's 20px and turns to mush).
  el.appendChild(
    path(
      "M5.5 42 L5.5 24 L9.5 24 L9.5 27.5 L13 27.5 L13 24 L17 24 L17 42",
      2.4,
    ),
  );
  el.appendChild(
    path(
      "M31 42 L31 24 L35 24 L35 27.5 L38.5 27.5 L38.5 24 L42.5 24 L42.5 42",
      2.4,
    ),
  );
  // The central keep, shoulders shared with the towers at x17 / x31 so the
  // three read as one connected building, not three objects.
  el.appendChild(
    path(
      "M17 42 L17 13 L21.5 13 L21.5 17 L26.5 17 L26.5 13 L31 13 L31 42",
      2.4,
    ),
  );
  el.appendChild(path("M4 42 L44 42", 2.4)); // the ground the whole silhouette stands on
  el.appendChild(filledPath("M21 42 L21 35.5 Q24 31.5 27 35.5 L27 42 Z")); // the gate
  return el;
}

/** Site-nav Your-games entry (nav-icon-candidate-placements task, graduated from the icon-lab
 *  Proposals exhibit): a hanging lantern — cap ring, hood, glass housing, and base, lit by a bright
 *  teardrop flame at its core. The idea: your played games are the rooms this light has already
 *  walked through. Andre's pick from the five-candidate Features round ("a clear winner") —
 *  displaces `createSkatCards` from the site-nav Your-games entry (`site-nav-enhance.js`) and the
 *  shared reference-entry table's own historyTitle row (`menu-entries.js`, feeding the pause menu
 *  and the start-screen strip); `createSkatCards` (in `prompt-icons.js`) goes back to meaning only
 *  "Pick up Skat" — its nav double-duty ends. Cap, hood, base, and flame are filled masses; the
 *  housing is stroked — at 20px the bright core inside a dark-edged housing is the whole read. A
 *  hanging ring, not a bail arc, and the hood/base flare wider than the housing: both load-bearing
 *  at 20px, since the first cut without them read as a padlock. `currentColor`, like the menu-nav
 *  marks beside it. See docs/components/MARKS.md § The Your-games mark. */
export function createLantern(size) {
  const el = svg(size, size);
  const ring = document.createElementNS(SVG_NS, "circle");
  ring.setAttribute("cx", 24);
  ring.setAttribute("cy", 7);
  ring.setAttribute("r", 2.8);
  ring.setAttribute("fill", "none");
  ring.setAttribute("stroke", "currentColor");
  ring.setAttribute("stroke-width", 2.2);
  el.appendChild(ring);
  el.appendChild(filledPath("M9 17.5 L39 17.5 L32 10.5 L16 10.5 Z")); // the hood
  el.appendChild(path("M15.5 17.5 L13 33 L35 33 L32.5 17.5", 2.4)); // the glass housing
  el.appendChild(filledPath("M10 33 L38 33 L36 39.5 L12 39.5 Z")); // the base
  // The flame — a teardrop, the mark's one bright mass.
  el.appendChild(
    filledPath(
      "M24 20 Q28.6 24.8 26.9 28.6 Q25.8 31 24 31.4 Q22.2 31 21.1 28.6 Q19.4 24.8 24 20 Z",
    ),
  );
  return el;
}

/** Site-nav Rules entry (nav-icon-candidate-placements task, graduated from the icon-lab Proposals
 *  exhibit): a crossroads signpost — two filled boards on a post, pointing opposite ways. The idea:
 *  Rules doesn't explain one thing, it points wherever a player needs — an object rather than a UI
 *  glyph. Displaces `createRulebook` from the site-nav Rules entry (`site-nav-enhance.js`) and the
 *  shared reference-entry table's own menuRules row (`menu-entries.js`, feeding the pause menu and
 *  the start-screen strip); `createRulebook` stays defined below, parked on the icon-lab Prompt &
 *  Feat tab (dev/CLAUDE.md's no-pruning rule). Andre prefers this mark to the rulebook outright ("i
 *  dont like/get the current icon... better is better"). Two boards, not three (a third closes the
 *  gaps between them and the mark collapses into a zigzag at 20px), drawn as filled masses rather
 *  than outlines — the family's fill-plus-stroke contrast, and what survives 20px where an outlined
 *  board turns to mush. The drawing round flagged the lower board's fill dissolving into the post
 *  at 20px as a flag misread; Andre's own read supersedes that concern — he doesn't read it as a
 *  flag at any size. `currentColor`, like the menu-nav marks beside it. See
 *  docs/components/MARKS.md § The Rules mark. */
export function createFingerpost(size) {
  const el = svg(size, size);
  el.appendChild(path("M24 8 L24 42", 3)); // the post
  el.appendChild(path("M17.5 42 L30.5 42", 2.4)); // the ground
  el.appendChild(filledPath("M23 11 L37 11 L42 17 L37 23 L23 23 Z")); // upper board, pointing right
  el.appendChild(filledPath("M25 27 L11 27 L6 33 L11 39 L25 39 Z")); // lower board, pointing left
  return el;
}

const QUARTERED_SHIELD_OUTLINE =
  "M8 8 L40 8 L40 25 Q40 36.5 24 42.5 Q8 36.5 8 25 Z";

/** Site-nav Help entry (nav-icon-candidate-placements task, graduated from the icon-lab Proposals
 *  exhibit): a heater shield, quartered checky (first and fourth filled). Displaces `createScroll`
 *  from the site-nav Help entry (`site-nav-enhance.js`) and the shared reference-entry table's own
 *  menuHelp row (`menu-entries.js`, feeding the pause menu and the start-screen strip);
 *  `createScroll` stays defined below and has since found its own home — the Menu's Nachschlagen
 *  section header (`game-menu.js`, profile-overlay-and-menu-slim), where it marks the whole
 *  reference group rather than competing for one entry in it.
 *  Andre: the scroll read as the weakest mark in the row, and the Help page can
 *  be argued as a defense — something to shelter behind, in some way — which the shield states
 *  directly. The quartering is drawn checky because that's the heraldic pattern that survives
 *  smallest — two solid masses and a shield outline, no charges to mush. `currentColor`, like the
 *  menu-nav marks beside it. See docs/components/MARKS.md § The Help mark. */
export function createQuarteredShield(size) {
  const el = svg(size, size);
  el.appendChild(filledPath("M8.5 8.5 L24 8.5 L24 24 L8.5 24 Z")); // first quarter
  el.appendChild(filledPath("M24 24 L39.5 24 L39.5 25 Q39.5 36 24 41.8 Z")); // fourth quarter
  el.appendChild(path(QUARTERED_SHIELD_OUTLINE, 2.6));
  el.appendChild(path("M24 8 L24 41", 2.2)); // the pale
  el.appendChild(path("M8.6 24 L39.4 24", 2.2)); // the fess
  return el;
}

/** One key's bow (the ring end) — shared by both keys `createCrossedKeys` below draws. */
function keyBow(cx, cy) {
  const c = document.createElementNS(SVG_NS, "circle");
  c.setAttribute("cx", cx);
  c.setAttribute("cy", cy);
  c.setAttribute("r", 6.2);
  c.setAttribute("fill", "none");
  c.setAttribute("stroke", "currentColor");
  c.setAttribute("stroke-width", 3);
  return c;
}

/** The credential mark (nav-icon-candidate-placements task, graduated from the icon-lab Proposals
 *  exhibit): the castellan's keys, a heraldic saltire cut — bows low, bits pointing up (deliberately
 *  — bits pointing inward read as a bow tie, and up keeps the silhouette apart from crossed blades,
 *  which are card content, the Jack motif, off-limits as chrome). The idea: a credential is what
 *  opens something — login, registration, a password. **Two homes**, one factory, both of them the
 *  profile overlay's Konto section header at its two registration states (profile-overlay-and-menu-slim):
 *  (1) anonymous — the static header over the credential rows
 *  (`game-start-identity-overlay.js`), and (2) account — the collapsible Konto section carrying the
 *  change-password form (`game-start-profile-sections.js`; the identity column's Knight-bust
 *  headline stays the identity mark, the keys mark what the password fields open).
 *  The two doorway homes retired there: the Menu's identity entry now wears `createPersonaKnight`,
 *  since a stable "Profil" entry is about who you are rather than what it opens, and the anonymous
 *  overlay's headline shed its mark when the Konto header took over the job.
 *  `currentColor`, like the menu-nav marks beside it. See docs/components/MARKS.md § Keys. */
export function createCrossedKeys(size) {
  const el = svg(size, size);
  // Key one: bow lower-left, shaft rising to the upper right, two bits biting OUTWARD off the
  // shaft's far end (pointing inward reads as two more crossing strokes — a bow tie).
  el.appendChild(keyBow(10.5, 37.5));
  el.appendChild(path("M14.8 33.2 L35 13", 3));
  el.appendChild(path("M27.5 20.5 L31.6 24.6 M31.5 16.5 L35.6 20.6", 2.8));
  // Key two: the mirror.
  el.appendChild(keyBow(37.5, 37.5));
  el.appendChild(path("M33.2 33.2 L13 13", 3));
  el.appendChild(path("M20.5 20.5 L16.4 24.6 M16.5 16.5 L12.4 20.6", 2.8));
  return el;
}

/** Retired from the site-nav/pause-menu Help entry in favor of `createQuarteredShield` above
 *  (nav-icon-candidate-placements task) — kept, not deleted, per dev/CLAUDE.md's no-pruning rule:
 *  still importable, still on the icon-lab Prompt & Feat tab. An unrolled parchment scroll — rolled
 *  cylindrical ends framing a flat sheet, two ruled lines standing in for the reference text within.
 *  currentColor, like the gate pair and the menu mark: the host button recolors it. See
 *  docs/components/MARKS.md § The Help mark. */
export function createScroll(size) {
  const el = svg(size, size);
  el.appendChild(path("M8 12 Q4 12 4 18 L4 30 Q4 36 8 36", 2.5));
  el.appendChild(path("M40 12 Q44 12 44 18 L44 30 Q44 36 40 36", 2.5));
  el.appendChild(path("M8 12 L40 12", 2.5));
  el.appendChild(path("M8 36 L40 36", 2.5));
  el.appendChild(path("M14 20 L34 20", 2));
  el.appendChild(path("M14 26 L30 26", 2));
  return el;
}

/** Retired from the site-nav/pause-menu Rules entry in favor of `createFingerpost` above
 *  (nav-icon-candidate-placements task) — kept, not deleted, per dev/CLAUDE.md's no-pruning rule:
 *  still importable, still on the icon-lab Prompt & Feat tab. A closed, clasped rulebook — cover,
 *  spine, and the buckled strap holding it shut. Closed rather than open (help-rules-menu-overlays
 *  task): the Help scroll owns "unrolled reference sheet" and the History ledger
 *  (`createAnnalsBook`, in `prompt-icons.js`) owns "open pages of records" — the rulebook is
 *  neither; it's the settled law you consult, not page through, so the clasp (not a page) is the
 *  read. currentColor, like the Help scroll and the menu mark: the host button recolors it. See
 *  docs/components/MARKS.md § The Rules mark. */
export function createRulebook(size) {
  const el = svg(size, size);
  const cover = document.createElementNS(SVG_NS, "rect");
  cover.setAttribute("x", "9");
  cover.setAttribute("y", "8");
  cover.setAttribute("width", "27");
  cover.setAttribute("height", "33");
  cover.setAttribute("rx", "2");
  cover.setAttribute("fill", "none");
  cover.setAttribute("stroke", "currentColor");
  cover.setAttribute("stroke-width", "2.2");
  el.appendChild(cover);
  el.appendChild(path("M17 8 L17 41", 2)); // the spine, a hair in from the cover's left edge
  const clasp = document.createElementNS(SVG_NS, "rect");
  clasp.setAttribute("x", "19");
  clasp.setAttribute("y", "21");
  clasp.setAttribute("width", "11");
  clasp.setAttribute("height", "6");
  clasp.setAttribute("rx", "1.5");
  clasp.setAttribute("fill", "currentColor");
  el.appendChild(clasp);
  return el;
}

/** Pause-menu Strategy entry: a compass rose — a ringed dial with a needle pointing the way.
 *  currentColor, like the menu-nav marks beside it: the host button recolors it.
 *  Chosen over reusing the spectacles (already the hint button's "ask what Lothar sees" — the
 *  Help mark's own overload precedent) and over the History ledger/Rules book (both already
 *  single-meaning at the time — the Rules book, `createRulebook` above, has since retired from the
 *  Rules entry to `createFingerpost`, nav-icon-candidate-placements task, but the "already
 *  single-meaning" reasoning that ruled it out here doesn't depend on where it currently ships):
 *  a compass is neither a reference sheet nor settled law, it's *direction* — the strategy pages
 *  point you toward the right play. See docs/components/MARKS.md § The Strategy mark. */
export function createCompass(size) {
  const el = svg(size, size);
  const ring = document.createElementNS(SVG_NS, "circle");
  ring.setAttribute("cx", "24");
  ring.setAttribute("cy", "24");
  ring.setAttribute("r", "16");
  ring.setAttribute("fill", "none");
  ring.setAttribute("stroke", "currentColor");
  ring.setAttribute("stroke-width", "2.2");
  el.appendChild(ring);
  el.appendChild(filledPath("M24 9 L28 24 L24 39 L20 24 Z")); // the needle, N-S
  el.appendChild(filledDot(24, 24, 2.4)); // the pivot
  return el;
}

/** Pause-menu Glossary entry (glossary-page design § Overlay wiring): a closed reference book with
 *  three staggered alphabet index tabs along its fore-edge — the classic "look a word up" cut,
 *  distinct from every other book-shaped mark in this family: `createRulebook`'s cover is clasped
 *  shut (no tabs — settled law you consult whole, not a word you look up; retired from the Rules
 *  entry to `createFingerpost`, nav-icon-candidate-placements task, but kept for this comparison),
 *  the History ledger (`createAnnalsBook`, in `prompt-icons.js`) is open with ruled entry lines
 *  (records, not definitions), and `createScroll` is a rolled sheet, not a book at all (itself
 *  retired from the Help entry to `createQuarteredShield`, same task). currentColor, like its
 *  three siblings — the host button recolors it. See docs/components/MARKS.md § The Glossary mark. */
export function createTabbedTome(size) {
  const el = svg(size, size);
  const cover = document.createElementNS(SVG_NS, "rect");
  cover.setAttribute("x", "8");
  cover.setAttribute("y", "8");
  cover.setAttribute("width", "28");
  cover.setAttribute("height", "33");
  cover.setAttribute("rx", "2");
  cover.setAttribute("fill", "none");
  cover.setAttribute("stroke", "currentColor");
  cover.setAttribute("stroke-width", "2.2");
  el.appendChild(cover);
  el.appendChild(path("M16 8 L16 41", 2)); // the spine, the same inset as the rulebook's
  for (const y of [14, 21, 28]) {
    const tab = document.createElementNS(SVG_NS, "rect");
    tab.setAttribute("x", "36");
    tab.setAttribute("y", String(y));
    tab.setAttribute("width", "5");
    tab.setAttribute("height", "4");
    tab.setAttribute("rx", "1");
    tab.setAttribute("fill", "currentColor");
    el.appendChild(tab);
  }
  return el;
}

/** The multiplayer mark (multiplayer-entry-and-notifications design § "The section", settled
 *  2026-08-10 after a five-round candidate process): a turned three-legged stool — an elliptical
 *  seat over three splayed legs. Three legs, one seat — it only stands if all three are there,
 *  the game's own shape; "seat" is also the multiplayer model's own noun (seat occupancy,
 *  docs/MULTIPLAYER). Joins the craft-object register the chalice, strongbox, counting frame,
 *  balance scale, and bid weight established rather than widening the visual surface (DESIGN
 *  Principle 12's constructive form). The benign misread as a small table is a feature, not a
 *  bug — "table" is the product's own literal noun (open a table, "{n} Tische warten"). Marks the
 *  start-screen section bar and the in-game Menu's Multiplayer entry. currentColor, like the menu
 *  mark and gate pair beside it — the host bar/button recolors it. The seat is a filled mass (the
 *  family's fill-plus-stroke contrast, like the rulebook's clasp) and the legs splay wide — both
 *  load-bearing at 24px: a shallower splay lets the three legs merge into one blob. See
 *  docs/components/MARKS.md § The Multiplayer mark for the rejected alternates. */
export function createThreeLeggedStool(size) {
  const el = svg(size, size);
  const seat = document.createElementNS(SVG_NS, "ellipse");
  seat.setAttribute("cx", "24");
  seat.setAttribute("cy", "18");
  seat.setAttribute("rx", "14.5");
  seat.setAttribute("ry", "5.6");
  seat.setAttribute("fill", "currentColor");
  el.appendChild(seat);
  el.appendChild(path("M17.6 22.4 Q13.6 30 11 38.8", 2.8));
  el.appendChild(path("M30.4 22.4 Q34.4 30 37 38.8", 2.8));
  el.appendChild(line(24, 22.8, 24, 39.4, 2.8));
  return el;
}

/** Pause-menu Leaderboard entry (leaderboard-dedicated-page-design § The overlay + menu entry;
 *  Andre's pick after the alternatives round, 2026-08-10): the victor's dais — a stepped podium
 *  (three risers, tallest centre) with a small planted pennant on the top step. Vocabulary:
 *  rank-as-height plus the "planted means this is mine" claim-posture idiom the standard family
 *  (outcome-icons.js/role-icons.js) also draws on — a board position asserted, not just displayed
 *  — but this pennant is its own small shape, not built on that family's primitives or its
 *  actor/glyph clothColor contract (docs/components/MARKS.md § The Leaderboard mark has the
 *  full non-membership note and the no-overload check against createOutcomeStandardUp/Down, which
 *  can share the same overlay when a row's stats-v2 fragment is open). `currentColor`, like its
 *  menu siblings (createQuarteredShield/createFingerpost/createCompass/createTabbedTome) — the
 *  host button recolors it. Rejected alternates from the same round (worth
 *  naming so nobody re-proposes them): a laurel wreath (two mirrored leaf-blade branches) — the
 *  first working concept, legible at 48/96px but collapsed to a fringed U at the 24px menu-row
 *  size; an honour stele (an arch-topped tablet with three ruled lines) — read as a record rather
 *  than an honour, and at 24px converged visually on the Help scroll's own rounded-box-plus-lines
 *  silhouette two rows above it; a throne (a domed high-backed seat) — legible at every size but
 *  read as generic furniture at 24px, with no heritage signal left once the crest/crown detail that
 *  would supply one is small enough to mush (and would collide with createMenuIcon crowning the
 *  same overlay). See docs/components/MARKS.md § The Leaderboard mark. */
export function createVictorDais(size) {
  const el = svg(size, size);
  el.appendChild(
    path("M6 42 L6 30 L18 30 L18 22 L30 22 L30 33 L42 33 L42 42 Z", 2.4),
  );
  el.appendChild(path("M24 22 L24 7", 2.4));
  el.appendChild(filledDot(24, 5.8, 1.9));
  el.appendChild(filledPath("M24 8 L37 11.5 L32 15 L37 18.5 L24 15 Z"));
  return el;
}
